import React, { Component } from "react";
import PropTypes from "prop-types";
import cn from "classnames";
import Menu from "components/Menu";
import Switcher from "components/Switcher";
import SwitcherPane from "components/Switcher/Pane";
import ExternalClickListener from "components/ExternalClickListener";
import Chip from "components/Chip";
import QueryManagerContext from "context/QueryManager/QueryManagerContext";
import MinMaxSlider from "../MinMaxSlider";
import EstimateWarning from "./EstimateWarning";
import styles from "./styles.module.scss";

export default class PriceFilter extends Component {
  queryStringKeys = [
    "minMonthlyPrice",
    "maxMonthlyPrice",
    "minPricePerSquareFoot",
    "maxPricePerSquareFoot",
    "activePriceFilter",
  ];

  constructor(props, context) {
    super(props);
    this.committedValues = context.values;
    this.state = {
      minMonthlyPrice: context.values.minMonthlyPrice,
      maxMonthlyPrice: context.values.maxMonthlyPrice,
      minPricePerSquareFoot: context.values.minPricePerSquareFoot,
      maxPricePerSquareFoot: context.values.maxPricePerSquareFoot,
      activePriceFilter: context.values.activePriceFilter,
    };
  }

  componentDidUpdate() {
    const { open } = this.props;
    const { values } = this.context;
    const {
      minMonthlyPrice,
      maxMonthlyPrice,
      minPricePerSquareFoot,
      maxPricePerSquareFoot,
      activePriceFilter,
    } = this.state;
    const committedChanged = this.queryStringKeys.some(
      (key) => values[key] !== this.committedValues[key]
    );
    this.committedValues = values;
    if (
      (!open || committedChanged) &&
      (values.minMonthlyPrice !== minMonthlyPrice ||
        values.maxMonthlyPrice !== maxMonthlyPrice ||
        values.maxPricePerSquareFoot !== maxPricePerSquareFoot ||
        values.minPricePerSquareFoot !== minPricePerSquareFoot ||
        values.activePriceFilter !== activePriceFilter)
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        minMonthlyPrice: values.minMonthlyPrice,
        maxMonthlyPrice: values.maxMonthlyPrice,
        minPricePerSquareFoot: values.minPricePerSquareFoot,
        maxPricePerSquareFoot: values.maxPricePerSquareFoot,
        activePriceFilter: values.activePriceFilter,
      });
    }
  }

  handleClear = () => {
    const { defaultValues } = this.context;
    this.setState(
      {
        minMonthlyPrice: defaultValues.minMonthlyPrice,
        maxMonthlyPrice: defaultValues.maxMonthlyPrice,
        minPricePerSquareFoot: defaultValues.minPricePerSquareFoot,
        maxPricePerSquareFoot: defaultValues.maxPricePerSquareFoot,
        activePriceFilter: defaultValues.activePriceFilter,
      },
      this.onDone
    );
  };

  handleMonthlyPriceChange = ({ min, max }) => {
    const { defaultValues } = this.context;
    this.setState({
      minMonthlyPrice: min,
      maxMonthlyPrice: max,
      minPricePerSquareFoot: defaultValues.minPricePerSquareFoot,
      maxPricePerSquareFoot: defaultValues.maxPricePerSquareFoot,
      activePriceFilter: "MONTHLY",
    });
  };

  handlePricePerSquareFootChange = ({ min, max }) => {
    const { defaultValues } = this.context;
    this.setState({
      minPricePerSquareFoot: min,
      maxPricePerSquareFoot: max,
      minMonthlyPrice: defaultValues.minMonthlyPrice,
      maxMonthlyPrice: defaultValues.maxMonthlyPrice,
      activePriceFilter: "PSF",
    });
  };

  get isDefault() {
    const { keysAreDefault, values } = this.context;
    return keysAreDefault(this.queryStringKeys, values);
  }

  get mode() {
    const { values } = this.context;
    if (values.activePriceFilter === "PSF") return "Per Square Foot";

    return "Monthly";
  }

  get chipText() {
    if (this.isDefault) return "Price";
    const { values, defaultValues } = this.context;
    const min =
      this.mode === "Monthly"
        ? values.minMonthlyPrice
        : values.minPricePerSquareFoot;

    const max =
      this.mode === "Monthly"
        ? values.maxMonthlyPrice
        : values.maxPricePerSquareFoot;

    const ceiling =
      this.mode === "Monthly"
        ? defaultValues.maxMonthlyPrice
        : defaultValues.maxPricePerSquareFoot;

    return `$${min} - $${max}${max >= ceiling ? "+" : ""}`;
  }

  onSwitcherChange = (title) => {
    this.setState({
      activePriceFilter: title === "Monthly" ? "MONTHLY" : "PSF",
    });
  };

  onDone = (toggle = true) => {
    const { update, defaultValues } = this.context;
    const { onToggle } = this.props;
    const {
      minPricePerSquareFoot,
      maxPricePerSquareFoot,
      minMonthlyPrice,
      maxMonthlyPrice,
      activePriceFilter,
    } = this.state;

    update({
      minPricePerSquareFoot:
        minPricePerSquareFoot || defaultValues.minPricePerSquareFoot,
      maxPricePerSquareFoot:
        maxPricePerSquareFoot === ""
          ? defaultValues.maxPricePerSquareFoot
          : maxPricePerSquareFoot,
      minMonthlyPrice: minMonthlyPrice || defaultValues.minMonthlyPrice,
      maxMonthlyPrice:
        maxMonthlyPrice === ""
          ? defaultValues.maxMonthlyPrice
          : maxMonthlyPrice,
      page: 1,
      activePriceFilter: activePriceFilter || defaultValues.activePriceFilter,
    });
    if (toggle) onToggle();
  };

  render() {
    const { activePriceFilter } = this.state;
    const { open, onToggle } = this.props;
    const { keysAreDefault, defaultValues, values } = this.context;
    const {
      minMonthlyPrice,
      maxMonthlyPrice,
      minPricePerSquareFoot,
      maxPricePerSquareFoot,
    } = this.state;

    return (
      <ExternalClickListener onExternalClick={() => open && this.onDone()}>
        <div className={styles.filterWrapper}>
          <Chip
            data-qa="price-filter-button"
            className={cn(styles.chip, !this.isDefault && styles.chipSelected)}
            onClick={onToggle}
            active={open}
          >
            {this.chipText}
          </Chip>
          <Menu
            data-qa="price-filter-menu"
            title="Price"
            className={styles.priceMenu}
            visible={open}
            onClose={onToggle}
            onClear={this.handleClear}
            onDone={this.onDone}
            isDefault={keysAreDefault(this.queryStringKeys, values)}
          >
            <Switcher
              onSwitch={this.onSwitcherChange}
              selectedPane={
                activePriceFilter === "PSF" ? "Per Square Foot" : "Monthly"
              }
            >
              <SwitcherPane title="Monthly" default={this.mode === "Monthly"}>
                <MinMaxSlider
                  min={minMonthlyPrice}
                  floor={defaultValues.minMonthlyPrice}
                  max={maxMonthlyPrice}
                  ceiling={defaultValues.maxMonthlyPrice}
                  onChange={this.handleMonthlyPriceChange}
                  onSliderMove={this.handleMonthlyPriceChange}
                  prefixSign="$"
                />
                <EstimateWarning />
              </SwitcherPane>
              <SwitcherPane
                title="Per Square Foot"
                default={this.mode === "Per Square Foot"}
              >
                <MinMaxSlider
                  min={minPricePerSquareFoot}
                  floor={defaultValues.minPricePerSquareFoot}
                  max={maxPricePerSquareFoot}
                  ceiling={defaultValues.maxPricePerSquareFoot}
                  onChange={this.handlePricePerSquareFootChange}
                  onSliderMove={this.handlePricePerSquareFootChange}
                  prefixSign="$"
                />
                <EstimateWarning />
              </SwitcherPane>
            </Switcher>
          </Menu>
        </div>
      </ExternalClickListener>
    );
  }
}

PriceFilter.propTypes = {
  open: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

PriceFilter.contextType = QueryManagerContext;
